import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DESTINATION_DICTIONARY } from '../../dictionary/route-dictionary.enum';
import { MetaTags } from './meta-tags';
import { TranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MetaTagsService {
  private DESTINATION_TO_METATAGS = new Map<string, MetaTags>();
  private translation;
  private defaultTags: MetaTags;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private metaService: Meta
  ) {
    translateService.get('METATAGS').subscribe((translation) => {
      this.translation = translation;
      this.initializeMetaTagsDictionary();
    });

    router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const matchedDestinationKey = this.findMatchedDestinationKey(this.router.url);
      if (matchedDestinationKey) {
        this.setMetaTags(this.DESTINATION_TO_METATAGS[matchedDestinationKey]);
      } else {
        this.setMetaTags(this.defaultTags);
      }
    });
  }

  private findMatchedDestinationKey(route: string) {
    const destinationEntries = Array.from(DESTINATION_DICTIONARY.entries());
    const matchedDestination = destinationEntries.find(
      (destination) => destination[1] === this.router.url
    );
    return matchedDestination ? matchedDestination[0] : null;
  }

  private initializeMetaTagsDictionary() {
    Array.from(DESTINATION_DICTIONARY.keys()).forEach((key) =>
      this.DESTINATION_TO_METATAGS.set(key, null)
    );
    this.defaultTags = this.translation['DEFAULT'];
    this.defaultTags.propOgImage = environment.REDIRECT_URI + 'assets/images/ic-logo.jpg';
    Array.from(this.DESTINATION_TO_METATAGS.keys()).forEach((destination) => {
      this.DESTINATION_TO_METATAGS[destination] = { ...this.defaultTags };

      if (this.translation[destination]) {
        (this.DESTINATION_TO_METATAGS[destination] as MetaTags).title = this.translation[
          destination
        ].title
          ? this.translation[destination].title
          : this.defaultTags.title;
        (this.DESTINATION_TO_METATAGS[destination] as MetaTags).description = this.translation[
          destination
        ].description
          ? this.translation[destination].description
          : this.defaultTags.description;
        (this.DESTINATION_TO_METATAGS[destination] as MetaTags).propOgTitle = this.translation[
          destination
        ].propOgTitle
          ? this.translation[destination].propOgTitle
          : this.translation[destination].title;
        (this.DESTINATION_TO_METATAGS[destination] as MetaTags).propOgDescription = this
          .translation[destination].propOgDescription
          ? this.translation[destination].propOgDescription
          : this.translation[destination].description;
        if (this.translation[destination].twitterCard) {
          (this.DESTINATION_TO_METATAGS[destination] as MetaTags).twitterCard =
            this.translation[destination].twitterCard;
        }
        if (this.translation[destination].twitterSite) {
          (this.DESTINATION_TO_METATAGS[destination] as MetaTags).twitterSite =
            this.translation[destination].twitterSite;
        }
        if (this.translation[destination].twitterCreator) {
          (this.DESTINATION_TO_METATAGS[destination] as MetaTags).twitterCreator =
            this.translation[destination].twitterCreator;
        }
        if (this.translation[destination].propOgType) {
          (this.DESTINATION_TO_METATAGS[destination] as MetaTags).propOgType =
            this.translation[destination].propOgType;
        }
        if (this.translation[destination].propOgImage) {
          (this.DESTINATION_TO_METATAGS[destination] as MetaTags).propOgImage =
            this.translation[destination].propOgImage;
        }
      }
    });
  }

  getDefaultMetaTags(): MetaTags {
    return { ...this.defaultTags };
  }

  getTagsForCurrentRoute(): MetaTags {
    const matchedDestinationKey = this.findMatchedDestinationKey(this.router.url);
    return matchedDestinationKey
      ? { ...this.DESTINATION_TO_METATAGS[matchedDestinationKey] }
      : null;
  }

  setMetaTags(metaTags: MetaTags) {
    this.metaService.updateTag(
      { name: 'description', content: metaTags.description },
      'name="description"'
    );
    this.metaService.updateTag(
      {
        property: 'og:title',
        content: metaTags.propOgTitle
          ? metaTags.propOgTitle === this.defaultTags.title
            ? this.defaultTags.title
            : this.defaultTags.title + ' | ' + metaTags.propOgTitle
          : metaTags.title === this.defaultTags.title
          ? this.defaultTags.title
          : this.defaultTags.title + ' | ' + metaTags.title
      },
      'property="og:title"'
    );
    this.metaService.updateTag(
      {
        property: 'og:description',
        content: metaTags.propOgDescription ? metaTags.propOgDescription : metaTags.description
      },
      'property="og:description"'
    );
    this.metaService.updateTag(
      {
        name: 'twitter:card',
        content: metaTags.twitterCard ? metaTags.twitterCard : this.defaultTags.twitterCard
      },
      'name="twitter:card"'
    );
    this.metaService.updateTag(
      {
        name: 'twitter:site',
        content: metaTags.twitterSite ? metaTags.twitterSite : this.defaultTags.twitterSite
      },
      'name="twitter:site"'
    );
    this.metaService.updateTag(
      {
        name: 'twitter:creator',
        content: metaTags.twitterCreator
          ? metaTags.twitterCreator
          : this.defaultTags.twitterCreator
      },
      'name="twitter:creator"'
    );
    this.metaService.updateTag(
      {
        property: 'og:type',
        content: metaTags.propOgType ? metaTags.propOgType : this.defaultTags.propOgType
      },
      'property="og:type"'
    );
    this.metaService.updateTag(
      {
        property: 'og:url',
        content: environment.REDIRECT_URI + this.router.url.replace(/^\//, '').split('?', 1)
      },
      'property="og:url"'
    );
    this.metaService.updateTag(
      {
        property: 'og:image',
        content: metaTags.propOgImage ? metaTags.propOgImage : this.defaultTags.propOgImage
      },
      'property="og:image"'
    );
  }
}
